import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Feed as FeedClient, FeedStoreState } from '@knocklabs/client';
import {
  useKnockClient,
  useNotificationStore,
  useNotifications,
} from '@knocklabs/react';

import { Nullable } from '@/utils/globalTypes';

type FeedAnchorElement = Nullable<HTMLElement>;

type InAppNotificationFeedContext = {
  items: FeedStoreState['items'];
  isLoadingNotificationFeed: boolean;
  isLoadingMoreNotificationFeed: boolean;
  hasNotificationItems: boolean;
  hasNextPage: boolean;
  hasError: boolean;
  feedClient: FeedClient;
  unreadCount: number;
  unseenCount: number;
  totalCount: number;
  feedAnchorEl: FeedAnchorElement;
  setFeedAnchorEl: (el: FeedAnchorElement) => void;
};

const KNOCK_CHANNEL_ID = import.meta.env.VITE_KNOCK_FEED_CHANNEL_ID ?? '';
const FEED_DEFAULT_PAGE_SIZE = 20;

export const InAppNotificationFeedContext =
  createContext<InAppNotificationFeedContext | null>(null);

const InAppNotificationFeedProvider = ({
  children,
  accountId,
}: PropsWithChildren<{ accountId?: string }>) => {
  const [anchorEl, setAnchorEl] = useState<FeedAnchorElement>(null);

  const feedFilters = useMemo(
    () => ({
      account_hashid: accountId,
    }),
    [accountId],
  );

  const knockClient = useKnockClient();
  const feedClient = useNotifications(knockClient, KNOCK_CHANNEL_ID, {
    page_size: FEED_DEFAULT_PAGE_SIZE,
    trigger_data: feedFilters,
  });
  const { items, metadata, loading, networkStatus } =
    useNotificationStore(feedClient);

  useEffect(() => {
    feedClient.fetch();
  }, [feedClient]);

  const setFeedAnchorEl = useCallback((el: FeedAnchorElement) => {
    setAnchorEl(el);
  }, []);

  const isLoadingNotificationFeed = loading && networkStatus === 'loading';
  const isLoadingMoreNotificationFeed = networkStatus === 'fetchMore';
  const hasError = networkStatus === 'error';

  const context: InAppNotificationFeedContext = useMemo(
    () => ({
      items,
      feedClient,
      isLoadingNotificationFeed,
      isLoadingMoreNotificationFeed,
      hasError,
      hasNotificationItems: items.length > 0,
      hasNextPage: metadata.total_count > items.length,
      unreadCount: metadata.unread_count,
      unseenCount: metadata.unseen_count,
      totalCount: metadata.total_count,
      feedAnchorEl: anchorEl,
      setFeedAnchorEl,
    }),
    [
      anchorEl,
      feedClient,
      isLoadingMoreNotificationFeed,
      isLoadingNotificationFeed,
      hasError,
      items,
      metadata.total_count,
      metadata.unread_count,
      metadata.unseen_count,
      setFeedAnchorEl,
    ],
  );

  return (
    <InAppNotificationFeedContext.Provider value={context}>
      {children}
    </InAppNotificationFeedContext.Provider>
  );
};

export default InAppNotificationFeedProvider;
